import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';
import { mockAuthHeaders } from '../utils/mockAuth';

/**
 * Hook for polling bullet generation status independently of the conversation.
 *
 * Polls GET /conversations/{id}/bullets every `interval` ms while enabled.
 * Stops when bullets_status === "ready", resumes when triggerPoll() is called.
 */
export default function useBulletsPolling(conversationId, { interval = 4000, enabled = true } = {}) {
  const [bullets, setBullets] = useState([]);
  const [bulletsStatus, setBulletsStatus] = useState('ready');
  const [pollGeneration, setPollGeneration] = useState(0);

  const triggerPoll = useCallback(() => {
    setBulletsStatus('pending');
    setPollGeneration(g => g + 1);
  }, []);

  useEffect(() => {
    if (!conversationId || !enabled) return;
    if (bulletsStatus === 'ready' && pollGeneration === 0) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/profile/enhancement/conversations/${conversationId}/bullets`,
          {
            headers: { ...mockAuthHeaders() },
            credentials: 'include',
          }
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;

        setBullets(data.bullets || []);
        setBulletsStatus(data.bullets_status || 'ready');

        if (data.bullets_status !== 'ready') {
          timerId = setTimeout(poll, interval);
        }
      } catch {
        if (!cancelled) {
          timerId = setTimeout(poll, interval * 2);
        }
      }
    };

    let timerId = setTimeout(poll, 800);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [conversationId, enabled, pollGeneration, interval, bulletsStatus]);

  return {
    bullets,
    bulletsStatus,
    isGenerating: bulletsStatus === 'pending' || bulletsStatus === 'generating',
    triggerPoll,
    setBullets,
  };
}
